/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useLocation } from "react-router"
import { useHistory } from "react-router-dom"
import { Button, Empty, Table } from "@duxton-web/ui"

import { useTranslationsMap } from "common/utils/i18n"

import { ReturnRefundStatusTag } from "ecommerce/components/ReturnRefundStatusTag"
import { ReturnRequestDrawer } from "ecommerce/components/ReturnRequestDrawer"
import { RETURN_WORKLIST_STATUS_GROUP_FILTER_OPTIONS } from "ecommerce/constants/returnRefund"
import { useReturnRequestDetail } from "ecommerce/hooks/useReturnRequestDetail"
import { useListReturnRefundWorklistQuery } from "ecommerce/queries"
import {
  getTrackingCountdownSeconds,
  openTimelineDestination,
} from "ecommerce/utils/returnRefundHelpers"
import {
  EventAction,
  EventCategory,
  ReturnRefundEntryPoint,
  ReturnRefundFilterKey,
} from "constants/tracking"
import useSingleStoreUser from "hooks/useSingleStoreUser"
import { BusinessType } from "types/models/menu"
import { TroyWebTracker } from "utils/tracking"

import useFeedbackStorelist from "../FeedbackFilters/hooks/useFeedbackStoreList"
import {
  getDefaultEndTimeBrowser,
  getDefaultFromTimeBrowser,
} from "../Incidents/utils"
import { ReturnRefundListFilter } from "./ReturnRefundListFilter"
import {
  countActionRequiredRequests,
  getRequestIssueType,
  ReturnRefundRequestRow,
  ReturnRefundRowItem,
  toEpochMs,
  toReturnRefundRequestRows,
} from "./utils"
import styles from "./returnRefundStyles.modules.css"

/**
 * One line per claimed item inside an item-level cell. The item columns (name, quantity,
 * amount, status) each stack their own lines, so the shared class pins a common line box
 * and the lines stay aligned across columns as long as none of them wraps.
 */
const stackedItemCell = (
  row: ReturnRefundRequestRow,
  renderItem: (item: ReturnRefundRowItem) => React.ReactNode,
) =>
  row.items.map(item => (
    <div key={item.key} className={styles.itemLine}>
      {renderItem(item)}
    </div>
  ))

/**
 * Return/Refund worklist for the Feedback page: one row per request, with the claimed items
 * stacked as lines inside the item-level cells.
 *
 * Distinct from the Orders tab's request-level list, whose columns are request-level only —
 * here the items stay individually visible with their own quantity, amount and status.
 * Filter state lives in the URL query params, matching the Incidents tab so a filtered view
 * stays shareable.
 */
export const ReturnRefundList: FC = () => {
  const history = useHistory()
  const location = useLocation()
  const { isSingleStoreUser, storeID } = useSingleStoreUser()
  const text = useTranslationsMap({
    columnAmount: "ecommerce.sellerCentre.returns.columnAmount",
    columnDate: "ecommerce.sellerCentre.returns.columnDate",
    columnItemName: "ecommerce.sellerCentre.returns.columnItemName",
    columnQuantity: "ecommerce.sellerCentre.returns.columnQuantity",
    columnShortOrderNumber:
      "ecommerce.sellerCentre.returns.columnShortOrderNumber",
    columnStatus: "common.status",
    emptyState: "ecommerce.sellerCentre.returns.emptyState",
    errorRetry: "ecommerce.sellerCentre.returns.errorRetry",
    errorState: "ecommerce.sellerCentre.returns.errorState",
  })

  // `useTranslationsMap` returns a new `text` object every render, so a
  // `useMemo` keyed on it would recompute every render anyway.
  const columns = [
    {
      title: text.columnDate,
      dataIndex: "date",
      key: "date",
    },
    {
      title: text.columnShortOrderNumber,
      dataIndex: "shortOrderNumber",
      key: "shortOrderNumber",
    },
    {
      title: text.columnItemName,
      key: "itemName",
      render: (_: unknown, row: ReturnRefundRequestRow) =>
        stackedItemCell(row, item => item.itemName),
    },
    {
      title: text.columnQuantity,
      key: "quantity",
      render: (_: unknown, row: ReturnRefundRequestRow) =>
        stackedItemCell(row, item => item.quantity),
    },
    {
      title: text.columnAmount,
      key: "amount",
      render: (_: unknown, row: ReturnRefundRequestRow) =>
        stackedItemCell(row, item => item.amount),
    },
    {
      title: text.columnStatus,
      key: "status",
      // Per item line, since siblings in one request can disagree — one refunded, one
      // pending. An item without status still emits its (empty) line so the column's
      // rhythm holds.
      render: (_: unknown, row: ReturnRefundRequestRow) =>
        stackedItemCell(row, item =>
          item.statusContent ? (
            <ReturnRefundStatusTag
              label={item.statusContent}
              actionRequired={item.highlightStatus}
            />
          ) : null,
        ),
    },
  ]

  const queryParams = new URLSearchParams(location.search)

  const merchantID =
    queryParams.get("merchantID") || (isSingleStoreUser ? storeID ?? "" : "")

  const statusStr = queryParams.get("status") || ""
  const selectedStatus = useMemo(
    () => (statusStr ? statusStr.split(",") : []),
    [statusStr],
  )

  const fromTime = queryParams.get("fromTime") || getDefaultFromTimeBrowser()
  const endTime = queryParams.get("endTime") || getDefaultEndTimeBrowser()

  /*
   * The event category is not just tracking — `handleFilter` only applies its
   * `city`/`deliverOption` branch when the category is `feedbackHomePage`, and that branch
   * stringifies an absent city into `["undefined"]`, which matches no store. So this must
   * stay off `feedbackHomePage` (the hook's default); the business-type scope below is
   * honoured either way.
   *
   * Scoped to `BusinessType.Shop`, which covers mart and eCommerce. Returns only exist for
   * those, so an unscoped picker would list the merchant's food stores too and — via the
   * auto-select below — open the tab on one, firing a worklist request for a store that can
   * never have returns. A merchant with both is the common case, not the edge case. The
   * scope lives in the hook rather than in each call so no later search can drop it.
   */
  const {
    handleFilter,
    filteredList,
    isFetchingOutletsSearch,
  } = useFeedbackStorelist(EventCategory.returnRefundList, BusinessType.Shop)

  const handleQueryChange = useCallback(
    (params: Partial<Record<string, string | string[]>>) => {
      const newParams = new URLSearchParams(location.search)

      Object.entries(params).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          (Array.isArray(value) && value.length === 0)
        ) {
          newParams.delete(key)
        } else if (Array.isArray(value)) {
          newParams.set(key, value.join(","))
        } else {
          newParams.set(key, value)
        }
      })

      history.replace({
        search: newParams.toString() ? `?${newParams.toString()}` : "",
      })
    },
    [history, location.search],
  )

  useEffect(() => {
    if (!filteredList?.length) {
      handleFilter({})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run once on mount

  // Land on a store rather than an empty table when the tab is opened without one.
  useEffect(() => {
    if (!merchantID && filteredList?.length) {
      const initialMerchantID = filteredList[0]?.merchantID

      if (initialMerchantID) {
        handleQueryChange({ merchantID: initialMerchantID })
      }
    }
  }, [filteredList, merchantID, handleQueryChange])

  /*
   * The status filter comes from the URL, so it is untrusted input — the Select's options
   * are not a constraint on what can arrive here. Intersecting with the backend's own
   * allowlist means a crafted or stale link degrades to a narrower filter rather than
   * failing the whole request: the worklist validates `statusFilter` server-side and
   * rejects the entire call with invalid-argument on any unknown value, which would blank
   * the table rather than ignore the bad param.
   *
   * This also drops `all`, the Select's pseudo-option — it is not a backend value, and
   * omitting the filter entirely is what returns every status.
   */
  const statusFilter = selectedStatus.filter(status =>
    (RETURN_WORKLIST_STATUS_GROUP_FILTER_OPTIONS as string[]).includes(status),
  )

  const {
    data,
    isFetching,
    isError,
    refetch,
  } = useListReturnRefundWorklistQuery(
    {
      merchantID,
      submittedAtFrom: toEpochMs(fromTime),
      submittedAtTo: toEpochMs(endTime, true),
      statusFilter: statusFilter.length ? statusFilter : undefined,
    },
    { skip: !merchantID },
  )

  const rows = useMemo(() => toReturnRefundRequestRows(data?.requests), [data])

  /*
   * Fires once per store, after the FIRST fetch for that store resolves SUCCESSFULLY —
   * not once per mount, since a merchant can switch stores via the filter without
   * navigating away. A background refetch from a status/date filter change must not
   * refire this: it is a page-level "the merchant landed here" signal, and
   * `FILTER_RETURN_REFUND` covers filter changes separately.
   *
   * Deliberately NOT fired on a failed fetch: the spec defines no error representation
   * for this label set, so a failure would otherwise report `IS_EMPTY: 1` and zero
   * counts — indistinguishable from a genuinely empty result. Not firing at all leaves
   * no event rather than a misleading one, and (just as importantly) leaves
   * `pageViewFiredRef` unset, so a successful retry still gets its real PAGEVIEW.
   */
  const pageViewFiredRef = useRef(false)
  useEffect(() => {
    pageViewFiredRef.current = false
  }, [merchantID])

  useEffect(() => {
    if (pageViewFiredRef.current) {
      return
    }

    /*
     * `isFetching` matters alongside `data`: RTK Query keeps serving the PREVIOUS store's
     * cached result while a new store's query is in flight, so `data !== undefined` alone
     * is true immediately on a store switch — this would fire PAGEVIEW with the old
     * store's counts attributed to the new one and then suppress the real fire once the
     * new response lands.
     */
    if (isFetching || data === undefined) {
      return
    }

    pageViewFiredRef.current = true
    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackReturnRefundPage,
      action: EventAction.pageView,
      labels: {
        /*
         * `metadata.totalCount` is documented as respecting applied filters (unlike
         * `bucketCounts`, which is merchant-global and ignores `statusFilter`/date range),
         * so it is the correct full-scope count here — the response itself is capped at
         * 50 rows, same fallback pattern as the Orders tab's `COUNT_RETURN_REFUND_ORDERS`.
         */
        COUNT_REQUESTS: data?.metadata?.totalCount ?? rows.length,
        /*
         * Deliberately NOT `metadata.bucketCounts` — unlike the Orders tab's chips (which
         * carry no date filter), this page's own `DATE_RANGE` label scopes the view to a
         * merchant-selected window, and `bucketCounts` ignores that window entirely. Reading
         * it here would report an action-required count spanning the merchant's whole
         * history while claiming a narrower `DATE_RANGE`, which is a worse mismatch than the
         * page-cap this label already lives with — so it stays page-level.
         */
        COUNT_ACTION_REQUIRED: countActionRequiredRequests(data?.requests),
        DATE_RANGE: `${fromTime}..${endTime}`,
        IS_EMPTY: rows.length === 0 ? 1 : 0,
        ENTRY_POINT: ReturnRefundEntryPoint.feedback,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching, merchantID])

  /*
   * One event per row each time it (re)enters the visible set — tracked by `requestID`,
   * not by the `rows` array's identity, and REPLACED rather than accumulated on every
   * run. `rows` is a fresh array on every successful query response (including a
   * background refetch that returns the same requests), so keying on the array
   * reference alone would re-emit every already-seen row on each silent refresh; that
   * is the case this guards against.
   *
   * Deliberately NOT a lifetime-unique, ever-growing set: a status/date filter change
   * can hide a row and later bring it back without remounting this component, and that
   * re-appearance is a genuine new impression (its countdown and status may well have
   * moved on by then) — not a duplicate of the earlier one. Only a refetch that leaves
   * the SAME rows visible is a duplicate.
   */
  const shownRequestIDsRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    const newlyShown = rows.filter(
      row => !shownRequestIDsRef.current.has(row.requestID),
    )

    newlyShown.forEach(row => {
      TroyWebTracker.sendEvent({
        category: EventCategory.feedbackReturnRefundPage,
        action: EventAction.returnRefundOrderShown,
        labels: {
          ORDER_ID: row.orderID,
          RETURN_REFUND_STATUS: row.requestStatus ?? null,
          ISSUE_TYPE: getRequestIssueType(row) ?? null,
          COUNT_ITEMS: row.items.length,
          COUNTDOWN_TIMER: getTrackingCountdownSeconds(row),
        },
      })
    })

    shownRequestIDsRef.current = new Set(rows.map(row => row.requestID))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows])

  /*
   * `COUNT_REQUESTS` is a snapshot of the table BEFORE this filter change takes effect —
   * the event is Click/Tap-triggered at the moment of change, ahead of the refetch, so
   * there is no "resulting" count available synchronously.
   *
   * The status `Select` below fires BOTH `onChange` and `onSelect`/`onDeselect` for one
   * user action (the latter pair exists only to cascade the "ALL" pseudo-option), so a
   * single status toggle calls this twice with the same key and value. Deduping against
   * the immediately-previous call collapses that into one event without touching the
   * select/deselect cascade logic shared with the Incidents tab.
   */
  const lastTrackedFilterRef = useRef<string>()
  useEffect(() => {
    lastTrackedFilterRef.current = undefined
  }, [merchantID])

  const trackFilterChange = (
    filterKey: ReturnRefundFilterKey,
    filterValue: string,
  ) => {
    const signature = `${filterKey}:${filterValue}`

    if (lastTrackedFilterRef.current === signature) {
      return
    }

    lastTrackedFilterRef.current = signature
    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackReturnRefundPage,
      action: EventAction.filterReturnRefund,
      labels: {
        FILTER: filterKey,
        FILTER_VALUE: filterValue,
        COUNT_REQUESTS: rows.length,
      },
    })
  }

  /*
   * The ORDER id — what the review-detail endpoint is keyed by — captured at
   * click time and never re-derived from the live list: a submitted decision can
   * move the request out of the current filter, and a per-render lookup that
   * dies with the row would unmount the open drawer mid-look. A row without one
   * has nothing to fetch and stays unclickable.
   */
  const [openOrderID, setOpenOrderID] = useState<string>()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const {
    detail: openDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
    refetch: refetchDetail,
    submitDecisions,
    submitInspections,
    requestClarification,
    uploadPhoto,
    removePhoto,
    isMediaBusy,
  } = useReturnRequestDetail({
    merchantID,
    orderID: openOrderID,
  })

  const handleRowClick = (row: ReturnRefundRequestRow) => {
    if (!row.orderID) {
      return
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.feedbackReturnRefundPage,
      action: EventAction.openReturnRefundDetail,
      labels: {
        ORDER_ID: row.orderID,
        RETURN_REFUND_STATUS: row.requestStatus ?? null,
        ISSUE_TYPE: getRequestIssueType(row) ?? null,
        COUNT_ITEMS: row.items.length,
      },
    })
    setOpenOrderID(row.orderID)
    setIsDrawerOpen(true)
  }

  return (
    <>
      <ReturnRefundListFilter
        storeList={filteredList}
        merchantID={merchantID}
        onChangeMerchantID={id => {
          trackFilterChange(ReturnRefundFilterKey.store, id)
          handleQueryChange({ merchantID: id })
        }}
        fromTime={fromTime}
        endTime={endTime}
        onChangeDateRange={(from, end) => {
          trackFilterChange(ReturnRefundFilterKey.dateRange, `${from}..${end}`)
          handleQueryChange({ fromTime: from, endTime: end })
        }}
        selectedStatus={selectedStatus}
        onChangeStatus={status => {
          trackFilterChange(
            ReturnRefundFilterKey.status,
            status.length ? status.join("|") : "ALL",
          )
          handleQueryChange({ status })
        }}
        isSingleStoreUser={isSingleStoreUser}
        handleFilter={handleFilter}
        isFetchingOutletsSearch={isFetchingOutletsSearch}
      />
      <Table
        columns={columns}
        dataSource={rows}
        rowKey="key"
        loading={isFetching}
        pagination={false}
        rowClassName={styles.clickableRow}
        onRow={(row: ReturnRefundRequestRow) => ({
          onClick: () => handleRowClick(row),
        })}
        locale={{
          emptyText: isError ? (
            /*
             * A failed fetch and a genuinely empty worklist both render zero rows, so
             * without this the merchant reads "no requests" when the truth is "not
             * loaded". The filters stay mounted above so the retry can be paired with
             * changing the store or date range.
             */
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={text.errorState}
            >
              <Button onClick={() => refetch()}>{text.errorRetry}</Button>
            </Empty>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={text.emptyState}
            />
          ),
        }}
      />
      <ReturnRequestDrawer
        open={isDrawerOpen}
        detail={openDetail}
        loading={isDetailLoading}
        error={isDetailError}
        onRetry={refetchDetail}
        onClose={() => setIsDrawerOpen(false)}
        onSubmitReview={submitDecisions}
        onSubmitInspection={submitInspections}
        onRequestClarification={requestClarification}
        onUploadPhoto={uploadPhoto}
        onRemovePhoto={removePhoto}
        isMediaBusy={isMediaBusy}
        /*
         * The fallback path only — a timeline action whose destination resolves is
         * rendered as a real anchor by `ReturnStatusTimeline` and never comes here.
         * What does reach it cannot validate (an absent destination — the support
         * hand-off pending BE's deeplink TODO and the chat slice — or a deeplink the
         * helpers could not unwrap), so `openTimelineDestination` is a guard that
         * stays a silent no-op rather than a path that opens anything today.
         */
        onContextualAction={(_action, _requestID, href) =>
          openTimelineDestination(href)
        }
      />
    </>
  )
}
